import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { useCallback, useState } from 'react';
import { projectAtom, SCOPES, useHasScope } from '@data/auth';
import isNullOrUndefined from '@utils/isNullOrUndefined';
import type { AxiosResponse, AxiosError } from 'axios';
import { useNotification } from '@data/notification';
import { useTranslation } from 'react-i18next';
import { environmentAtom } from '@data/fms/environment/states';
import type { Timetable, TimetableDetail } from '@data/fms/schedule/types';
import { useAtomCallback } from 'jotai/utils';

/**
 * 運行ダイヤ設定取得API
 */
export const useGetTimetablesAPI = (): {
  state: APIState;
  getVehicleTimetables: (vehicleId: string) => Promise<Timetable[]>;
  getTimetable: (timetableId: string) => Promise<TimetableDetail | null>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError } = useNotification();
  const fmsAPI = useFMSAPI();
  const getHasScope = useHasScope();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const getVehicleTimetablesAPI = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
    ): Promise<Result<Timetable[], AxiosResponse>> => {
      try {
        const res = await fmsAPI.get(
          `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/timetables`,
        );
        return new Success(res?.data?.timetables);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  const getTimetableAPI = useCallback(
    async (
      projectId: string,
      environmentId: string,
      timetableId: string,
    ): Promise<Result<TimetableDetail, AxiosResponse>> => {
      try {
        const res = await fmsAPI.get(
          `/${projectId}/environments/${environmentId}/timetables/${timetableId}`,
        );
        return new Success(res?.data);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  const getVehicleTimetables = useAtomCallback(
    useCallback(
      async (get, _, vehicleId: string) => {
        setState('loading');
        if (!getHasScope(SCOPES.DescribeSchedule)) {
          notifyError({
            message: t('api.fms.get_time_tables', { status: 'failed' }),
          });
          setState('hasError');
          return [];
        }
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) || isNullOrUndefined(environment)) {
          notifyError({
            message: t('api.fms.get_time_tables', { status: 'failed' }),
          });
          setState('hasError');
          return [];
        }
        const res = await getVehicleTimetablesAPI(
          project!.id,
          environment!.environment_id,
          vehicleId,
        );
        if (isNullOrUndefined(res.value) || res.isFailure()) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if ((res as any).value?.status !== 404) {
            notifyError({
              message: `${t('api.fms.get_time_tables', {
                status: 'failed',
              })}: ${getErrorMessage(res)}`,
            });
          }
          setState('hasError');
          return [];
        }
        setState('hasValue');
        return res.value;
      },
      [notifyError, getVehicleTimetablesAPI, getHasScope, t, getErrorMessage],
    ),
  );

  const getTimetable = useAtomCallback(
    useCallback(
      async (get, _, timetableId: string) => {
        setState('loading');
        if (!getHasScope(SCOPES.DescribeSchedule)) {
          notifyError({
            message: t('api.fms.get_time_tables', { status: 'failed' }),
          });
          setState('hasError');
          return null;
        }
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) || isNullOrUndefined(environment)) {
          notifyError({
            message: t('api.fms.get_time_tables', { status: 'failed' }),
          });
          setState('hasError');
          return null;
        }
        const res = await getTimetableAPI(
          project!.id,
          environment!.environment_id,
          timetableId,
        );
        if (isNullOrUndefined(res.value) || res.isFailure()) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if ((res as any).value?.status !== 404) {
            notifyError({
              message: `${t('api.fms.get_time_tables', {
                status: 'failed',
              })}: ${getErrorMessage(res)}`,
            });
          }
          setState('hasError');
          return null;
        }
        setState('hasValue');
        return res.value;
      },
      [notifyError, getTimetableAPI, getHasScope, t, getErrorMessage],
    ),
  );

  return {
    state,
    getVehicleTimetables,
    getTimetable,
  };
};
